import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axios-auth';
const initialState = {
    activeSessions: [],
    loading: 'idle',
    error: null,
    lastFetched: null,
};
// Async thunks
export const fetchActiveSessions = createAsyncThunk('session/fetchActiveSessions', async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get('/auth/sessions');
        return response.data.data;
    }
    catch (error) {
        console.error('Error fetching active sessions:', error);
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch active sessions');
    }
});
export const revokeAllSessions = createAsyncThunk('session/revokeAllSessions', async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/auth/sessions/revoke-all');
        return { revokedCount: response.data.revokedCount };
    }
    catch (error) {
        console.error('Error revoking all sessions:', error);
        return rejectWithValue(error.response?.data?.message || 'Failed to revoke all sessions');
    }
});
export const revokeSession = createAsyncThunk('session/revokeSession', async (sessionId, { rejectWithValue }) => {
    try {
        await axiosInstance.delete(`/api/auth/sessions/${sessionId}`);
        return sessionId;
    }
    catch (error) {
        console.error('Error revoking session:', error);
        return rejectWithValue(error.response?.data?.message || 'Failed to revoke session');
    }
});
// Session slice
const sessionSlice = createSlice({
    name: 'session',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSessions: (state) => {
            state.activeSessions = [];
            state.lastFetched = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch active sessions
            .addCase(fetchActiveSessions.pending, (state) => {
            state.loading = 'pending';
            state.error = null;
        })
            .addCase(fetchActiveSessions.fulfilled, (state, action) => {
            state.loading = 'succeeded';
            state.activeSessions = action.payload;
            state.lastFetched = Date.now();
            state.error = null;
        })
            .addCase(fetchActiveSessions.rejected, (state, action) => {
            state.loading = 'failed';
            state.error = action.payload || 'Failed to fetch active sessions';
        })
            // Revoke all sessions
            .addCase(revokeAllSessions.pending, (state) => {
            state.loading = 'pending';
            state.error = null;
        })
            .addCase(revokeAllSessions.fulfilled, (state, action) => {
            state.loading = 'succeeded';
            // Remove all sessions except current
            state.activeSessions = state.activeSessions.filter(session => session.is_current);
            state.error = null;
        })
            .addCase(revokeAllSessions.rejected, (state, action) => {
            state.loading = 'failed';
            state.error = action.payload || 'Failed to revoke all sessions';
        })
            // Revoke specific session
            .addCase(revokeSession.pending, (state) => {
            state.loading = 'pending';
            state.error = null;
        })
            .addCase(revokeSession.fulfilled, (state, action) => {
            state.loading = 'succeeded';
            // Remove the revoked session
            state.activeSessions = state.activeSessions.filter(session => session.id !== action.payload);
            state.error = null;
        })
            .addCase(revokeSession.rejected, (state, action) => {
            state.loading = 'failed';
            state.error = action.payload || 'Failed to revoke session';
        });
    },
});
// Export actions
export const { clearError, clearSessions } = sessionSlice.actions;
// Selectors
export const selectActiveSessions = (state) => state.session.activeSessions;
export const selectSessionLoading = (state) => state.session.loading;
export const selectSessionError = (state) => state.session.error;
export const selectCurrentSession = (state) => state.session.activeSessions.find((session) => session.is_current);
export const selectOtherSessions = (state) => state.session.activeSessions.filter((session) => !session.is_current);
export default sessionSlice.reducer;
